import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { TagChips, TagPills } from '../../components/common/EventTags';
import CompanyEventRow from './CompanyEventRow';
import CompanyFeed from './CompanyFeed';
import { ClubImage, ClubStatus } from './Clubs';
import { apiErrorText, apiFormRequest, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyFeed.css';
import './Clubs.css';

/* One club's page — what it is, who runs it, what it is doing, and the way in.

   Every colleague reads it; a member posts and registers; the club's admins (and the company's)
   also get the queue of requests, the roster and the editor. Planning the club's events is the
   company-events screens themselves: "Plan the club's events" turns the session into the club's
   (POST /clubs/<id>/manage), and the strip at the top of this page and the member home says so
   and offers the way back. */

const TABS = ['calendar', 'feed', 'photos', 'members'];

const Avatar = ({ url, name }) => (
  url ? <img className="club-avatar" src={url} alt="" />
    : <span className="club-avatar">{(name || '?').trim().charAt(0) || '?'}</span>
);

export const ManagingBar = ({ clubId, name }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const done = () => apiRequest('POST', '/clubs/leave-manage')
    .then((res) => { window.location.href = (res && res.redirect) || '/clubs'; })
    .catch(() => navigate('/member-home'));
  return (
    <div className="club-managing mb-3">
      <span><Icon name="users-gear" className="me-2" />{t('clubManagingBar', { name })}</span>
      <span className="club-actions">
        {clubId && (
          <button type="button" className="bravos-submit bravos-submit--ghost"
                  onClick={() => navigate(`/clubs/${clubId}`)}>{t('clubOpenPage')}</button>
        )}
        <button type="button" className="bravos-submit" onClick={done}>{t('clubDone')}</button>
      </span>
    </div>
  );
};

const ClubPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { clubId } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('calendar');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);       // {name, description, tags}
  const [requests, setRequests] = useState(null);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState([]);
  const [photos, setPhotos] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/clubs/${clubId}`).then(setData).catch(() => navigate('/clubs'));
  }, [clubId, navigate]);
  useEffect(() => { load(); }, [load]);

  const canManage = !!(data && data.can_manage);
  const loadRequests = useCallback(() => {
    if (!canManage) return;
    apiRequest('GET', `/clubs/${clubId}/requests`).then((r) => setRequests(r.requests || []))
      .catch(() => setRequests([]));
  }, [clubId, canManage]);
  useEffect(() => { if (tab === 'members') loadRequests(); }, [tab, loadRequests]);

  useEffect(() => {
    if (tab !== 'photos' || photos !== null) return;
    apiRequest('GET', `/company/photos?club=${encodeURIComponent(clubId)}&limit=60`)
      .then((r) => setPhotos(r.items || [])).catch(() => setPhotos([]));
  }, [tab, photos, clubId]);

  /* Colleagues to add, by name — debounced so typing does not fire a request per key. */
  useEffect(() => {
    if (!canManage || query.trim().length < 2) { setFound([]); return undefined; }
    const timer = setTimeout(() => {
      apiRequest('GET', `/clubs/${clubId}/colleagues?q=${encodeURIComponent(query.trim())}`)
        .then((r) => setFound(r.people || [])).catch(() => setFound([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, clubId, canManage]);

  const act = (promise, after) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then((res) => { if (after) after(res); load(); })
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  const join = () => act(apiRequest('POST', `/clubs/${clubId}/join`),
    () => setNotice(t('clubJoinRequested')));
  const withdraw = () => act(apiRequest('DELETE', `/clubs/${clubId}/join`));
  const leave = () => act(apiRequest('POST', `/clubs/${clubId}/leave`));
  const manage = () => act(apiRequest('POST', `/clubs/${clubId}/manage`),
    (res) => { window.location.href = (res && res.redirect) || '/programme'; });
  const answer = (eventId, status) => act(
    apiRequest('POST', `/company/event/${eventId}/rsvp`, { status }));
  const verdict = (reqId, v) => act(
    apiRequest('POST', `/clubs/${clubId}/requests/${reqId}/${v}`), loadRequests);
  const addMember = (personId, role) => act(
    apiRequest('POST', `/clubs/${clubId}/members`, { person_id: personId, role }),
    () => { setQuery(''); setFound([]); });
  const setRole = (personId, role) => act(
    apiRequest('PUT', `/clubs/${clubId}/members/${personId}`, { role }));
  const removeMember = (personId) => act(
    apiRequest('DELETE', `/clubs/${clubId}/members/${personId}`));
  const archive = (archived) => act(
    apiRequest('POST', `/clubs/${clubId}/archive`, { archived }));

  const save = () => act(apiRequest('PUT', `/clubs/${clubId}`, editing), () => setEditing(null));
  const upload = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    act(apiFormRequest(`/clubs/${clubId}/image`, form));
  };

  if (data === null) {
    return <Base activeNav="home"><Card><span className="auth__spinner" /></Card></Base>;
  }
  const club = data.club;
  const isMember = !!club.my_role;

  const eventRow = (e) => (
    <CompanyEventRow
      key={e.id}
      event={e}
      lang={i18n.language}
      onOpen={() => navigate(`/public-event/${e.id}`)}
      badges={(
        <>
          {e.members_only && <span className="ce-pill">{t('clubMembersOnlyLabel')}</span>}
          {e.rsvp && <span className="ce-pill ce-pill--going">{t('goingCount', { n: e.rsvp.going })}</span>}
          {e.my_rsvp === 'going' && (
            <span className="bravos-event__pill bravos-event__pill--in">{t('youAreComing')}</span>
          )}
        </>
      )}
    >
      {!e.is_past && !e.registration_closed && e.can_register && (
        <>
          <button type="button" className={`ce-tab ${e.my_rsvp === 'going' ? 'is-on' : ''}`}
                  onClick={(ev) => { ev.stopPropagation(); answer(e.id, 'going'); }} disabled={busy}>
            <Icon name="check" /> {t('imComing')}
          </button>
          <button type="button" className={`ce-tab ${e.my_rsvp === 'not_going' ? 'is-on' : ''}`}
                  onClick={(ev) => { ev.stopPropagation(); answer(e.id, 'not_going'); }} disabled={busy}>
            {t('imNotComing')}
          </button>
        </>
      )}
      {!e.is_past && !e.can_register && <span className="ce-note">{t('clubJoinToRegister')}</span>}
    </CompanyEventRow>
  );

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/clubs" />
        {data.managing && <div className="mt-3"><ManagingBar clubId={club.id} name={club.name} /></div>}
        <div className="club-hero mt-3">
          <ClubImage club={club} className="club-hero__img" />
          <div className="club-hero__title">
            <Title my="0">{club.name}</Title>
            <ClubStatus club={club} />
          </div>
          <TagPills tags={club.tags || []} />
          {club.description && <p className="club-hero__desc">{club.description}</p>}
          {data.admins.length > 0 && (
            <p className="ce-note mb-0">
              {t('clubAdmins')}: {data.admins.map((a) => a.name).join(', ')}
            </p>
          )}
          <div className="club-actions">
            {!isMember && !club.requested && !club.is_archived && (
              <button type="button" className="bravos-submit" onClick={join} disabled={busy}>
                <Icon name="user-add" /> {t('clubJoin')}
              </button>
            )}
            {!isMember && club.requested && (
              <button type="button" className="bravos-submit bravos-submit--ghost" onClick={withdraw} disabled={busy}>
                {t('clubCancelRequest')}
              </button>
            )}
            {canManage && !club.is_archived && (
              <button type="button" className="bravos-submit" onClick={manage} disabled={busy}>
                <Icon name="calendar-star" /> {t('clubManageEvents')}
              </button>
            )}
            {canManage && !editing && (
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setEditing({ name: club.name, description: club.description,
                                                  tags: (club.tags || []).map((x) => x.id) })}>
                <Icon name="edit" /> {t('clubEdit')}
              </button>
            )}
            {isMember && (
              <button type="button" className="bravos-submit bravos-submit--ghost" onClick={leave} disabled={busy}>
                <Icon name="sign-out-alt" /> {t('clubLeave')}
              </button>
            )}
          </div>
          {notice && <p className="bravos-cal__notice">{notice}</p>}
        </div>

        {editing && (
          <div className="club-form mt-3">
            <label htmlFor="club-name">{t('clubName')}</label>
            <input id="club-name" className="cf-input" maxLength={50} value={editing.name}
                   onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <label htmlFor="club-desc">{t('clubDescription')}</label>
            <textarea id="club-desc" className="cf-input" rows={4} maxLength={2000}
                      value={editing.description}
                      onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
            {(data.tags_catalog || []).length > 0 && (
              <>
                <label>{t('clubTags')}</label>
                <TagChips tags={data.tags_catalog} selected={editing.tags}
                          onToggle={(id) => setEditing({ ...editing,
                            tags: editing.tags.includes(id)
                              ? editing.tags.filter((x) => x !== id) : [...editing.tags, id] })} />
              </>
            )}
            <label htmlFor="club-img">{t('clubImage')}</label>
            <input id="club-img" type="file" accept="image/*"
                   onChange={(e) => upload(e.target.files && e.target.files[0])} />
            <div className="club-actions">
              <button type="button" className="bravos-submit" onClick={save} disabled={busy}>{t('save')}</button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setEditing(null)}>{t('cancel')}</button>
              {data.is_company_admin && (
                <button type="button" className="bravos-submit bravos-submit--ghost"
                        onClick={() => archive(!club.is_archived)} disabled={busy}>
                  {club.is_archived ? t('clubUnarchive') : t('clubArchive')}
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="ce-tabs">
          {TABS.map((key) => (
            <button key={key} type="button" className={`ce-tab ${tab === key ? 'is-on' : ''}`}
                    onClick={() => setTab(key)}>
              {t(`clubTab_${key}`)}
              {key === 'members' && canManage && data.pending_count > 0 && (
                <span className="ce-pill ms-1">{data.pending_count}</span>
              )}
            </button>
          ))}
        </div>
      </Card>

      {tab === 'calendar' && (
        <Card>
          {data.upcoming.length === 0 && data.past.length === 0 && (
            <p className="ce-note">{t('clubNoEvents')}</p>
          )}
          {data.upcoming.length > 0 && (
            <>
              <p className="bravos-cal__label">{t('upcoming')}</p>
              {data.upcoming.map(eventRow)}
            </>
          )}
          {data.past.length > 0 && (
            <>
              <p className="bravos-cal__label bravos-cal__label--past">{t('past')}</p>
              {data.past.map(eventRow)}
            </>
          )}
        </Card>
      )}

      {tab === 'feed' && <CompanyFeed clubId={club.id} />}

      {tab === 'photos' && (
        <Card>
          {photos === null ? <span className="auth__spinner" /> : photos.length === 0 ? (
            <p className="ce-note">{t('clubNoPhotos')}</p>
          ) : (
            <div className="club-photos">
              {photos.map((card) => {
                const src = (card.photo || {}).url || (card.post || {}).image_url;
                return src ? (
                  <a key={card.id} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" /></a>
                ) : null;
              })}
            </div>
          )}
        </Card>
      )}

      {tab === 'members' && (
        <>
          {canManage && (
            <Card>
              <p className="bravos-cal__label">{t('clubRequests')}</p>
              {requests === null ? <span className="auth__spinner" /> : requests.length === 0 ? (
                <p className="ce-note">{t('clubNoRequests')}</p>
              ) : (
                <ul className="club-people">
                  {requests.map((r) => r.person && (
                    <li key={r.id} className="club-person">
                      <Avatar url={r.person.photo_url} name={r.person.name} />
                      <span className="club-person__name">{r.person.name}
                        {r.person.job_title && <small>{r.person.job_title}</small>}
                      </span>
                      <span className="club-person__btns">
                        <button type="button" className="ce-tab is-on" disabled={busy}
                                onClick={() => verdict(r.id, 'accept')}>{t('accept')}</button>
                        <button type="button" className="ce-tab" disabled={busy}
                                onClick={() => verdict(r.id, 'decline')}>{t('decline')}</button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <p className="bravos-cal__label mt-3">{t('clubAddColleague')}</p>
              <input className="cf-input" value={query} placeholder={t('clubSearchColleague')}
                     onChange={(e) => setQuery(e.target.value)} />
              {found.length > 0 && (
                <ul className="club-people">
                  {found.map((p) => (
                    <li key={p.id} className="club-person">
                      <Avatar url={p.photo_url} name={p.name} />
                      <span className="club-person__name">{p.name}<small>{p.email}</small></span>
                      <span className="club-person__btns">
                        <button type="button" className="ce-tab" disabled={busy}
                                onClick={() => addMember(p.id, 'member')}>{t('add')}</button>
                        <button type="button" className="ce-tab" disabled={busy}
                                onClick={() => addMember(p.id, 'admin')}>{t('clubMakeAdmin')}</button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}
          <Card>
            <p className="bravos-cal__label">{t('clubMembersN', { n: data.members.length })}</p>
            <ul className="club-people">
              {data.members.map((m) => (
                <li key={m.id} className="club-person">
                  <Avatar url={m.photo_url} name={m.name} />
                  <span className="club-person__name">{m.name}
                    {m.is_admin && <span className="ce-pill ms-2">{t('clubAdminBadge')}</span>}
                    {m.job_title && <small>{m.job_title}</small>}
                  </span>
                  {canManage && (
                    <span className="club-person__btns">
                      <button type="button" className="ce-tab" disabled={busy}
                              onClick={() => setRole(m.id, m.is_admin ? 'member' : 'admin')}>
                        {m.is_admin ? t('clubMakeMember') : t('clubMakeAdmin')}
                      </button>
                      <button type="button" className="ce-tab" disabled={busy}
                              onClick={() => removeMember(m.id)}>{t('remove')}</button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
    </Base>
  );
};

export default ClubPage;
